/**
 * ARX Header - Mega Menu Hover Controller
 *
 * Manages hover interactions, keyboard navigation, and mobile drawer
 * for a fullwidth mega menu header component.
 */
(function () {
  'use strict';

  var CLOSE_DELAY = 100;
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function ArxHeader(root) {
    this.root = root;
    this.header = root.querySelector('.arx-header');
    this.navLinks = root.querySelectorAll('[data-nav-trigger]');
    this.megaMenus = root.querySelectorAll('[data-mega-menu]');
    this.overlay = root.querySelector('[data-overlay]');
    this.hamburger = root.querySelector('[data-drawer-toggle]');
    this.drawer = root.querySelector('[data-drawer]');
    this.drawerOverlay = root.querySelector('[data-drawer-overlay]');
    this.drawerClose = root.querySelector('[data-drawer-close]');
    this.closeTimer = null;
    this.activeMenuId = null;
    this._boundDrawerKeydown = this._handleDrawerKeydown.bind(this);
    this._boundDocumentClick = this._handleDocumentClick.bind(this);
    this.spacer = root.querySelector('.arx-header-spacer');
    this._bindDesktop();
    this._bindMobile();
    this._bindKeyboard();
    this._bindScroll();
  }

  /* ---- Sticky + Transparent scroll detection ---- */

  ArxHeader.prototype._bindScroll = function () {
    if (!this.header) return;

    var header = this.header;
    var spacer = this.spacer;
    var isSticky = header.classList.contains('arx-header--sticky');
    var isTransparent = header.classList.contains('arx-header--transparent');

    if (!isSticky && !isTransparent) return;

    var headerOffsetTop = 0;

    var calcOffset = function () {
      // When not fixed, measure the header's natural position
      if (!header.classList.contains('is-fixed')) {
        headerOffsetTop = header.offsetTop;
      }
    };

    var onScroll = function () {
      // Transparent background toggle
      if (isTransparent) {
        if (window.scrollY > 10) {
          header.classList.add('is-scrolled');
        } else {
          header.classList.remove('is-scrolled');
        }
      }

      // Sticky: switch to fixed when scrolled past header position
      if (isSticky) {
        if (window.scrollY >= headerOffsetTop) {
          header.classList.add('is-fixed');
          if (spacer) spacer.classList.add('is-active');
        } else {
          header.classList.remove('is-fixed');
          if (spacer) spacer.classList.remove('is-active');
        }
      }
    };

    calcOffset();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', calcOffset, { passive: true });
    onScroll();
  };

  /* ---- Desktop mega menu ---- */

  ArxHeader.prototype._bindDesktop = function () {
    var self = this;

    this.navLinks.forEach(function (link) {
      link.addEventListener('mouseenter', function () {
        self._openMenu(link);
      });
      link.addEventListener('focus', function () {
        self._openMenu(link);
      });
      link.addEventListener('mouseleave', function () {
        self._scheduleClose();
      });
    });

    this.megaMenus.forEach(function (menu) {
      menu.addEventListener('mouseenter', function () {
        self._cancelClose();
      });
      menu.addEventListener('mouseleave', function () {
        self._scheduleClose();
      });
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self._closeMenu();
      });
    }

    document.addEventListener('click', this._boundDocumentClick);
  };

  ArxHeader.prototype._handleDocumentClick = function (e) {
    if (this.activeMenuId === null) return;
    var clickedNav = e.target.closest('[data-nav-trigger]');
    var clickedMenu = e.target.closest('[data-mega-menu]');
    if (!clickedNav && !clickedMenu) {
      this._closeMenu();
    }
  };

  ArxHeader.prototype._updateMegaMenuTop = function () {
    if (!this.header) return;
    var rect = this.header.getBoundingClientRect();
    this.root.style.setProperty('--arx-mm-top', rect.bottom + 'px');
  };

  ArxHeader.prototype._openMenu = function (link) {
    this._cancelClose();
    var menuId = link.dataset.navTrigger;
    if (!menuId) return;

    this._updateMegaMenuTop();

    this.navLinks.forEach(function (l) {
      l.classList.remove('is-active');
      l.setAttribute('aria-expanded', 'false');
    });

    this.megaMenus.forEach(function (m) {
      m.classList.remove('is-open');
      m.classList.remove('is-closing');
    });

    link.classList.add('is-active');
    link.setAttribute('aria-expanded', 'true');

    var target = this.root.querySelector('[data-mega-menu="' + menuId + '"]');
    if (target) {
      target.classList.add('is-open');
    }

    this.activeMenuId = menuId;

    if (this.overlay) {
      this.overlay.classList.add('is-visible');
    }
  };

  ArxHeader.prototype._scheduleClose = function () {
    var self = this;
    this._cancelClose();
    this.closeTimer = setTimeout(function () {
      self._closeMenu();
    }, CLOSE_DELAY);
  };

  ArxHeader.prototype._cancelClose = function () {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  };

  ArxHeader.prototype._closeMenu = function () {
    this._cancelClose();

    this.navLinks.forEach(function (l) {
      l.classList.remove('is-active');
      l.setAttribute('aria-expanded', 'false');
    });

    this.megaMenus.forEach(function (m) {
      if (m.classList.contains('is-open')) {
        m.classList.add('is-closing');
        m.classList.remove('is-open');
        m.addEventListener('transitionend', function handler() {
          m.classList.remove('is-closing');
          m.removeEventListener('transitionend', handler);
        }, { once: true });
      }
    });

    if (this.overlay) {
      this.overlay.classList.remove('is-visible');
    }

    this.activeMenuId = null;
  };

  /* ---- Keyboard navigation ---- */

  ArxHeader.prototype._bindKeyboard = function () {
    var self = this;

    this.navLinks.forEach(function (link) {
      link.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          // Allow default link navigation
          return;
        }
        if (e.key === ' ') {
          e.preventDefault();
          var menuId = link.dataset.navTrigger;
          if (menuId && self.activeMenuId === menuId) {
            self._closeMenu();
          } else {
            self._openMenu(link);
            var target = self.root.querySelector('[data-mega-menu="' + link.dataset.navTrigger + '"]');
            if (target) {
              var firstFocusable = target.querySelector(FOCUSABLE_SELECTOR);
              if (firstFocusable) firstFocusable.focus();
            }
          }
        }
        if (e.key === 'Escape') {
          self._closeMenu();
          link.focus();
        }
      });
    });

    this.megaMenus.forEach(function (menu) {
      menu.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          var triggerId = menu.dataset.megaMenu;
          self._closeMenu();
          var trigger = self.root.querySelector('[data-nav-trigger="' + triggerId + '"]');
          if (trigger) trigger.focus();
        }
      });
    });

    this.megaMenus.forEach(function (menu) {
      menu.addEventListener('focusout', function (e) {
        if (!menu.contains(e.relatedTarget) && !self._isNavLink(e.relatedTarget)) {
          self._scheduleClose();
        }
      });
    });
  };

  ArxHeader.prototype._isNavLink = function (el) {
    if (!el) return false;
    return Array.prototype.some.call(this.navLinks, function (link) {
      return link === el;
    });
  };

  /* ---- Mobile drawer ---- */

  ArxHeader.prototype._bindMobile = function () {
    var self = this;

    if (this.hamburger) {
      this.hamburger.addEventListener('click', function () {
        self._toggleDrawer();
      });
    }

    if (this.drawerClose) {
      this.drawerClose.addEventListener('click', function () {
        self._closeDrawer();
      });
    }

    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', function () {
        self._closeDrawer();
      });
    }

    var triggers = this.root.querySelectorAll('[data-drawer-trigger]');
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var item = trigger.closest('[data-drawer-item]');
        if (item) {
          var isExpanded = item.classList.toggle('is-expanded');
          trigger.setAttribute('aria-expanded', String(isExpanded));
        }
      });
    });
  };

  ArxHeader.prototype._toggleDrawer = function () {
    if (this.drawer && this.drawer.classList.contains('is-open')) {
      this._closeDrawer();
    } else {
      this._openDrawer();
    }
  };

  ArxHeader.prototype._openDrawer = function () {
    if (this.drawer) {
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-modal', 'true');
      this.drawer.setAttribute('role', 'dialog');
    }
    if (this.drawerOverlay) this.drawerOverlay.classList.add('is-visible');
    if (this.hamburger) {
      this.hamburger.classList.add('is-open');
      this.hamburger.setAttribute('aria-expanded', 'true');
    }
    document.body.classList.add('arx-overflow-hidden');

    if (this.drawerClose) this.drawerClose.focus();

    document.addEventListener('keydown', this._boundDrawerKeydown);
  };

  ArxHeader.prototype._closeDrawer = function () {
    if (this.drawer) {
      this.drawer.classList.remove('is-open');
      this.drawer.removeAttribute('aria-modal');
      this.drawer.removeAttribute('role');
    }
    if (this.drawerOverlay) this.drawerOverlay.classList.remove('is-visible');
    if (this.hamburger) {
      this.hamburger.classList.remove('is-open');
      this.hamburger.setAttribute('aria-expanded', 'false');
      this.hamburger.focus();
    }
    document.body.classList.remove('arx-overflow-hidden');

    document.removeEventListener('keydown', this._boundDrawerKeydown);
  };

  ArxHeader.prototype._handleDrawerKeydown = function (e) {
    if (e.key === 'Escape') {
      this._closeDrawer();
      return;
    }

    if (e.key === 'Tab' && this.drawer) {
      var focusable = this.drawer.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  /* ---- Init ---- */

  function init() {
    var el = document.querySelector('[data-arx-header]');
    if (el) new ArxHeader(el);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
